import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
} from '@mui/icons-material';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import NavigationRoundedIcon from '@mui/icons-material/NavigationRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { DocumentUploadZone } from '../../components/tracking/DocumentUploadZone';
import { trackMyOrder } from '../../api/publicOrders';
import { EmptyState } from '../../components/ui/EmptyState';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';


type OrderStatus = 'PENDING' | 'IN_PROGRESS' | 'READY_FOR_PICKUP' | 'COMPLETED' | 'CANCELLED';

const statusTranslationKeys: Record<string, string> = {
  PENDING: 'status.pending',
  IN_PROGRESS: 'status.inProgress',
  READY_FOR_PICKUP: 'status.readyForPickup',
  COMPLETED: 'status.completed',
  CANCELLED: 'status.cancelled',
};

export const MyOrderTracking: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { trackingUUID } = useParams<{ trackingUUID: string }>();
  const queryClient = useQueryClient();

  const { data: tracking, isLoading, isError } = useQuery({
    queryKey: ['my-order-tracking', trackingUUID],
    queryFn: () => trackMyOrder(trackingUUID!),
    enabled: !!trackingUUID,
    retry: false,
    refetchInterval: 15000,
  });

  const unsubscribeMutation = useMutation({
    mutationFn: async () => {
      await axios.post(`${API_URL}/orders/track/${trackingUUID}/unsubscribe`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-order-tracking', trackingUUID] });
    },
  });

  const resubscribeMutation = useMutation({
    mutationFn: async () => {
      await axios.post(`${API_URL}/orders/track/${trackingUUID}/resubscribe`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-order-tracking', trackingUUID] });
    },
  });

  // Auto-refresh page meta tags
  useEffect(() => {
    document.title = tracking ? `${t('tracking.title')} - ${tracking.pharmacyChain.name}` : t('tracking.title');
  }, [tracking, t]);

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'PENDING':
        return '#9e9e9e'; // gray
      case 'IN_PROGRESS':
        return '#2196f3'; // blue
      case 'READY_FOR_PICKUP':
        return '#4caf50'; // green
      case 'COMPLETED':
        return '#1b5e20'; // dark green
      case 'CANCELLED':
        return '#f44336'; // red
      default:
        return '#9e9e9e';
    }
  };

  const getStatusLabel = (status: OrderStatus) => {
    return t(statusTranslationKeys[status] ?? 'status.pending');
  };

  const getStatusSteps = () => {
    return ['PENDING', 'IN_PROGRESS', 'READY_FOR_PICKUP', 'COMPLETED'];
  };

  const getCurrentStepIndex = (currentStatus: OrderStatus) => {
    const steps = getStatusSteps();
    const index = steps.indexOf(currentStatus);
    return index >= 0 ? index : 0;
  };

  if (isError) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 6, px: 3 }}>
        <EmptyState
          title="No encontramos este pedido"
          cta={{ label: 'Buscar medicamentos', to: '/buscar' }}
        />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!tracking) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pb: 4 }}>
      {/* Language Switcher */}
      <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 1000 }}>
        <LanguageSwitcher />
      </Box>

      {/* Status Banner */}
      <Box
        sx={{
          bgcolor: getStatusColor(tracking.status),
          color: 'white',
          py: 4,
          px: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" gutterBottom fontWeight="bold">
          {getStatusLabel(tracking.status)}
        </Typography>
        {tracking.status === 'READY_FOR_PICKUP' && (
          <Alert severity="success" sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              {t('tracking.prescriptionReady')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {t('tracking.pickUpAt')} {tracking.pharmacyChain.name}
            </Typography>
          </Alert>
        )}
        {tracking.status === 'COMPLETED' && (
          <Typography variant="h6" sx={{ mt: 2 }}>
            {t('app.thankYou')}
          </Typography>
        )}
      </Box>

      {/* Main Content */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, mt: 4 }}>
        {/* Status Timeline */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('tracking.orderProgress')}
          </Typography>
          <Stepper activeStep={getCurrentStepIndex(tracking.status)} alternativeLabel sx={{ mt: 3 }}>
            {getStatusSteps().map((step) => {
              const statusLog = tracking.statusTimeline.find((log) => log.status === step);
              return (
                <Step key={step}>
                  <StepLabel>
                    <Box>
                      <Typography variant="body2">{getStatusLabel(step as OrderStatus)}</Typography>
                      {statusLog && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(statusLog.timestamp).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'en-US')}
                        </Typography>
                      )}
                    </Box>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
          </CardContent></Card>

        {/* Medications List */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('tracking.yourMedications')}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('tracking.medication')}</TableCell>
                  <TableCell align="center">{t('tracking.qty')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tracking.medications.map((med, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} sx={{ color: 'text.headline' }}>
                        {med.productName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="medium">
                        {med.quantity}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          </CardContent></Card>

        {/* Document Upload */}
        {trackingUUID && tracking && (
          <Box sx={{ mt: 4, mb: 3 }}>
            <Typography variant="h6" sx={{ color: 'text.headline', mb: 2 }}>
              Documentos para la farmacia
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Sube tu cédula y carnet de seguro si necesitas cobertura. Estos archivos se
              guardan en tu perfil para futuros pedidos.
            </Typography>
            <DocumentUploadZone
              trackingUUID={trackingUUID}
              type="CEDULA"
              label="Cédula o pasaporte"
              description="Necesario si tu pedido contiene medicamentos controlados."
              initialUrl={tracking.nationalIdImageUrl}
            />
            <DocumentUploadZone
              trackingUUID={trackingUUID}
              type="INSURANCE_CARD"
              label="Carnet de seguro"
              description="Opcional — la farmacia puede procesar tu cobertura si lo subes."
              initialUrl={tracking.insuranceIdImageUrl}
            />
          </Box>
        )}

        {/* Pharmacy Information */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('tracking.pharmacyInfo')}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'primary.softBg',
                  color: 'primary.dark',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <StorefrontRoundedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h5" sx={{ color: 'text.headline', lineHeight: 1.2 }}>
                  {tracking.pharmacyChain.name}
                </Typography>
                {tracking.fulfillmentType === 'PICKUP' && tracking.pickupBranch && (
                  <>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', mt: 0.5, fontWeight: 500 }}
                    >
                      {tracking.pickupBranch.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      {tracking.pickupBranch.address}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {(() => {
              const isPickup = tracking.fulfillmentType === 'PICKUP';
              const branch = tracking.pickupBranch;
              const whatsappPhone =
                (isPickup && branch?.phoneNumber) || tracking.pharmacyChain.phoneNumber || null;
              const hasWaze = isPickup && !!branch;
              if (!hasWaze && !whatsappPhone) return null;
              return (
                <Box
                  sx={{
                    mt: 2.5,
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {hasWaze && branch && (
                    <Button
                      component="a"
                      href={
                        typeof branch.latitude === 'number' &&
                        typeof branch.longitude === 'number'
                          ? `https://waze.com/ul?ll=${branch.latitude}%2C${branch.longitude}&navigate=yes`
                          : `https://waze.com/ul?q=${encodeURIComponent(branch.address)}&navigate=yes`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      size="medium"
                      startIcon={<NavigationRoundedIcon />}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 600,
                        color: '#0066CC',
                        borderColor: '#0066CC55',
                        '&:hover': { bgcolor: 'primary.softBg', borderColor: 'primary.main' },
                      }}
                    >
                      {t('tracking.openInWaze')}
                    </Button>
                  )}
                  {whatsappPhone && (
                    <Button
                      component="a"
                      href={`https://wa.me/${whatsappPhone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      size="medium"
                      startIcon={<WhatsAppIcon />}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 600,
                        bgcolor: '#25D366',
                        color: '#fff',
                        boxShadow: '0 4px 12px -4px rgba(37,211,102,0.5)',
                        '&:hover': {
                          bgcolor: '#1FB855',
                          boxShadow: '0 6px 16px -4px rgba(37,211,102,0.6)',
                        },
                      }}
                    >
                      {t('tracking.whatsappPharmacy')}
                    </Button>
                  )}
                </Box>
              );
            })()}
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('tracking.notificationPreferences')}
          </Typography>
          <Box sx={{ mt: 2 }}>
            {tracking.notificationsEnabled ? (
              <Alert
                severity="info"
                icon={<NotificationsIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  {t('tracking.subscribedMessage')}
                </Typography>
              </Alert>
            ) : (
              <Alert
                severity="warning"
                icon={<NotificationsOffIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  {t('tracking.unsubscribedMessage')}
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {tracking.notificationsEnabled ? (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<NotificationsOffIcon />}
                  onClick={() => unsubscribeMutation.mutate()}
                  disabled={unsubscribeMutation.isPending}
                >
                  {unsubscribeMutation.isPending ? t('tracking.unsubscribing') : t('tracking.unsubscribe')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<NotificationsIcon />}
                  onClick={() => resubscribeMutation.mutate()}
                  disabled={resubscribeMutation.isPending}
                >
                  {resubscribeMutation.isPending ? t('tracking.resubscribing') : t('tracking.resubscribe')}
                </Button>
              )}
            </Box>

            {(unsubscribeMutation.isSuccess || resubscribeMutation.isSuccess) && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  {tracking.notificationsEnabled
                    ? t('tracking.resubscribeSuccess')
                    : t('tracking.unsubscribeSuccess')}
                </Typography>
              </Alert>
            )}
          </Box>
          </CardContent></Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {t('tracking.orderId')}: {tracking.trackingUUID.substring(0, 8)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t('tracking.lastUpdated')}: {new Date(tracking.updatedAt).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'en-US')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t('tracking.autoRefresh')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
